import { useEffect, useRef, useState } from "react";

// A drag of files rather than of selected text, a link, or an in-page element.
const carriesFiles = (transfer: DataTransfer | null): transfer is DataTransfer =>
  Boolean(transfer?.types.includes("Files"));

// A live drag fires `dragover` on a ~350ms cadence even while the pointer holds
// still, so silence for appreciably longer than that means the drag is gone.
// Backstops the window-exit `dragleave`, which some browsers never fire.
const DRAG_IDLE_MS = 1000;

// Makes the whole page a drop target for files, returning whether a file drag is
// currently over the window so the caller can raise a hint.
//
// `dragenter`/`dragleave` fire once per element the pointer crosses, so the
// nesting depth is counted and the hint only clears once the drag has left the
// window entirely. While `disabled`, drags are left to the browser's default
// handling — matching the attach button, which is disabled at the same times.
export function usePageFileDrop(onFiles: (files: File[]) => void, disabled: boolean): boolean {
  const depth = useRef(0);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);

  // Held in a ref so the listeners bind once per `disabled` change; rebinding on
  // every render would reset the depth count mid-drag.
  const onFilesRef = useRef(onFiles);
  useEffect(() => {
    onFilesRef.current = onFiles;
  }, [onFiles]);

  useEffect(() => {
    if (disabled) return;

    let idleTimer: ReturnType<typeof setTimeout> | undefined;

    const end = () => {
      clearTimeout(idleTimer);
      depth.current = 0;
      setIsDraggingFiles(false);
    };

    const keepAlive = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(end, DRAG_IDLE_MS);
    };

    const onDragEnter = (event: DragEvent) => {
      if (!carriesFiles(event.dataTransfer)) return;
      event.preventDefault();
      depth.current += 1;
      keepAlive();
      setIsDraggingFiles(true);
    };

    // `dragover` is the authoritative "still dragging" signal — it keeps
    // arriving as long as the drag is live. Raising the hint here as well as on
    // `dragenter` means an idle timeout that fired early (or a depth count that
    // hit zero while the drag was still over the window) self-corrects on the
    // next event rather than leaving the hint down for the rest of the drag.
    const onDragOver = (event: DragEvent) => {
      const transfer = event.dataTransfer;
      if (!carriesFiles(transfer)) return;
      // Without preventDefault the browser refuses the drop and opens the file
      // in place of the page.
      event.preventDefault();
      transfer.dropEffect = "copy";
      if (depth.current === 0) depth.current = 1;
      setIsDraggingFiles(true);
      keepAlive();
    };

    // Deliberately not gated on `carriesFiles`: a leave can report empty
    // `types`, and skipping the decrement would strand the count above zero and
    // leave the scrim covering the page.
    const onDragLeave = () => {
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) end();
    };

    const onDrop = (event: DragEvent) => {
      const transfer = event.dataTransfer;
      if (!carriesFiles(transfer)) return;
      event.preventDefault();
      end();
      const files = Array.from(transfer.files);
      if (files.length > 0) onFilesRef.current(files);
    };

    document.addEventListener("dragenter", onDragEnter);
    document.addEventListener("dragover", onDragOver);
    document.addEventListener("dragleave", onDragLeave);
    document.addEventListener("drop", onDrop);

    // The hint comes down with the listeners, so disabling mid-drag (a run
    // starting) can't strand it over the page.
    return () => {
      document.removeEventListener("dragenter", onDragEnter);
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("dragleave", onDragLeave);
      document.removeEventListener("drop", onDrop);
      end();
    };
  }, [disabled]);

  return isDraggingFiles;
}
